'use client';
import {
  LOCALE_LABELS,
  SUPPORTED_LOCALES,
  normalizeLocale,
  type SupportedLocale,
} from '@/i18n/config';
import {
  getPendingSignupLocale,
  setPendingSignupLocale,
} from '@/lib/auth-locale-preference';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocale } from 'next-intl';
import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { handleAuthError as handleAuthErrorBase } from '@/lib/auth-error-handler';
import { getAuthPageCopy } from '@/i18n/auth-page-copy';
import { useRouter, useSearchParams } from 'next/navigation';
import { toast } from 'sonner';
import { normalizeSignUpEmail } from './sign-up-email';
import type { PublicSignupPage } from './types';

// Keep the existing password restriction (min length 6) as requested
const signUpSchema = z
  .object({
    name: z
      .string()
      .min(2, 'Please enter your full name')
      .max(120, 'Name must be 120 characters or fewer'),
    email: z.string().email('Please enter a valid email address'),
    password: z.string().min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

const deriveNameFromEmail = (email: string): string => {
  if (!email) {
    return '';
  }
  const [localPart = ''] = email.split('@');
  const segments = localPart
    .replace(/[._-]+/g, ' ')
    .split(' ')
    .map((segment) => segment.replace(/\d+/g, '').trim())
    .filter((segment) => segment.length > 0)
    .map((segment) =>
      segment.length > 1
        ? segment[0]?.toUpperCase() + segment.slice(1).toLowerCase()
        : segment.toUpperCase()
    );
  return segments.join(' ');
};

export type SignUpFormType = z.infer<typeof signUpSchema>;

export interface SignUpFormProps {
  onToggleMode: () => void;
  onPhoneAuth: () => void;
  inviteToken?: string | null;
  authBasePath?: string;
  signupPage?: PublicSignupPage | null;
}
export function useSignUpState(props: SignUpFormProps) {
  const {
    onToggleMode,
    inviteToken,
    authBasePath = '/auth',
    signupPage,
  } = props;

  const locale = useLocale();

  const copy = getAuthPageCopy(locale);

  const handleAuthError = (error: unknown) =>
    handleAuthErrorBase(error, {
      emailNotVerified: copy.pleaseVerifyEmail,
      invalidCredentials: copy.invalidCredentials,
      invalidPassword: copy.invalidPasswordTryAgain,
      invalidVerificationCode: copy.invalidVerificationCode,
      verificationCodeExpired: copy.verificationCodeExpired,
    });

  const searchParams = useSearchParams();

  const router = useRouter();

  const returnTo = searchParams.get('returnTo');

  const localeParam = searchParams.get('locale');

  const fallbackLanguage = normalizeLocale(localeParam ?? locale ?? 'en');

  const oauthErrorCode = searchParams.get('error');

  const oauthErrorDescription = searchParams.get('error_description');

  const [isLoading, setIsLoading] = useState(false);

  const [pendingAction, setPendingAction] = useState<
    'google' | 'credentials' | 'verify' | 'resend' | null
  >(null);

  const [error, setError] = useState<string | null>(null);

  const [hasEditedName, setHasEditedName] = useState(false);

  const [selectedLanguage, setSelectedLanguage] = useState<SupportedLocale>(
    () => getPendingSignupLocale() ?? fallbackLanguage
  );

  const [hasExplicitLanguageSelection, setHasExplicitLanguageSelection] =
    useState(() => localeParam !== null || getPendingSignupLocale() !== null);

  const [languageSelectionDeferred, setLanguageSelectionDeferred] =
    useState(false);

  const lastOAuthErrorRef = React.useRef<string | null>(null);

  // Get step from URL, default to 1
  const stepParam = searchParams.get('step');

  const step = stepParam === '2' ? 2 : stepParam === '3' ? 3 : 1;

  // Get email from URL for steps 2 and 3
  const emailParam = searchParams.get('email');

  const pendingEmail = normalizeSignUpEmail(emailParam);

  const [pendingPassword, setPendingPassword] = useState<string>('');

  const [otpCode, setOtpCode] = useState<string>('');

  const sortedLocales = useMemo(
    () =>
      [...SUPPORTED_LOCALES].sort((left, right) =>
        LOCALE_LABELS[left].localeCompare(LOCALE_LABELS[right], undefined, {
          sensitivity: 'base',
        })
      ),
    []
  );

  const oauthSignupErrorMessage =
    oauthErrorCode === 'TEMP_EMAIL_BLOCKED' ||
    oauthErrorCode === 'TEMP_EMAIL_DOMAIN_BLOCKED'
      ? 'Please use a permanent email address. Temporary email providers are not allowed.'
      : typeof oauthErrorDescription === 'string' &&
          oauthErrorDescription.trim().length > 0
        ? oauthErrorDescription.trim()
        : null;

  const form = useForm<SignUpFormType>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      name: '',
      email: pendingEmail || '',
      password: '',
      confirmPassword: '',
    },
  });

  // Update form email when URL changes
  React.useEffect(() => {
    if (pendingEmail && pendingEmail !== form.getValues('email')) {
      form.setValue('email', normalizeSignUpEmail(pendingEmail));
    }
  }, [pendingEmail, form]);

  React.useEffect(() => {
    if (step === 2 && !hasEditedName) {
      const derived = deriveNameFromEmail(form.getValues('email'));
      const trimmed = derived.trim().slice(0, 120);
      if (trimmed) {
        form.setValue('name', trimmed, {
          shouldDirty: false,
          shouldValidate: false,
        });
      }
    }
  }, [step, form, hasEditedName]);

  React.useEffect(() => {
    const preferredLocale = getPendingSignupLocale() ?? fallbackLanguage;

    setSelectedLanguage((currentLanguage) =>
      currentLanguage === preferredLocale ? currentLanguage : preferredLocale
    );

    if (getPendingSignupLocale()) {
      setHasExplicitLanguageSelection(true);
    }
  }, [fallbackLanguage, locale, localeParam]);

  React.useEffect(() => {
    if (step !== 1 || !oauthSignupErrorMessage) {
      return;
    }

    if (lastOAuthErrorRef.current === oauthSignupErrorMessage) {
      return;
    }

    lastOAuthErrorRef.current = oauthSignupErrorMessage;
    setError(oauthSignupErrorMessage);
    toast.error(oauthSignupErrorMessage);
  }, [oauthSignupErrorMessage, step]);

  const handleEmailChange = (
    nextValue: string,
    onChange: (value: string) => void
  ) => {
    onChange(normalizeSignUpEmail(nextValue));
    if (error) {
      setError(null);
    }
  };

  const handleEmailBlur = (
    fieldValue: string,
    onBlur: () => void,
    onChange: (value: string) => void
  ) => {
    const normalizedEmail = normalizeSignUpEmail(fieldValue);
    if (normalizedEmail !== fieldValue) {
      onChange(normalizedEmail);
    }
    onBlur();
  };

  const handleLanguageSelect = (nextLanguage: string): void => {
    const normalizedLanguage = setPendingSignupLocale(nextLanguage);
    setSelectedLanguage(normalizedLanguage);
    setHasExplicitLanguageSelection(true);
    setLanguageSelectionDeferred(false);
  };

  const applyFieldErrors = (nextFieldErrors: Record<string, string>) => {
    for (const [fieldName, message] of Object.entries(nextFieldErrors)) {
      if (
        fieldName === 'name' ||
        fieldName === 'email' ||
        fieldName === 'password' ||
        fieldName === 'confirmPassword'
      ) {
        form.setError(fieldName, { type: 'server', message });
      }
    }
  };

  const buildSignInParams = (): URLSearchParams => {
    const params = new URLSearchParams();
    if (hasExplicitLanguageSelection) {
      params.set('locale', selectedLanguage);
    }
    if (returnTo) {
      params.set('returnTo', returnTo);
    }
    return params;
  };

  const buildSigningInParams = (): URLSearchParams => {
    const params = buildSignInParams();
    params.set('signupFlow', '1');
    return params;
  };
  return {
    onToggleMode,
    inviteToken,
    authBasePath,
    signupPage,
    locale,
    copy,
    handleAuthError,
    searchParams,
    router,
    returnTo,
    localeParam,
    fallbackLanguage,
    oauthErrorCode,
    oauthErrorDescription,
    isLoading,
    setIsLoading,
    pendingAction,
    setPendingAction,
    error,
    setError,
    hasEditedName,
    setHasEditedName,
    selectedLanguage,
    setSelectedLanguage,
    hasExplicitLanguageSelection,
    setHasExplicitLanguageSelection,
    languageSelectionDeferred,
    setLanguageSelectionDeferred,
    lastOAuthErrorRef,
    stepParam,
    step,
    emailParam,
    pendingEmail,
    pendingPassword,
    setPendingPassword,
    otpCode,
    setOtpCode,
    sortedLocales,
    oauthSignupErrorMessage,
    form,
    handleEmailChange,
    handleEmailBlur,
    handleLanguageSelect,
    applyFieldErrors,
    buildSignInParams,
    buildSigningInParams,
  };
}
